/* eslint-disable max-len */

// utils and constants
import findKey from 'lodash/findKey';

import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import SettingsPage from 'app/shared/containers/SettingsPage/Loadable';
import TwoFactorAuthPage from 'app/shared/containers/TwoFactorAuthPage/Loadable';
import FeatureFlagPage from 'app/shared/containers/FeatureFlagPage/Loadable';
import legacyRouteRedirects from 'app/shared/utils/legacyRouteRedirects';
import StringConstants from 'app/shared/constants/StringConstants';
import QueryConstants from 'app/shared/constants/QueryConstants';
import ListingClaimPage from 'app/shared/containers/ListingClaimPage/Loadable';
import queryUtils from 'app/shared/utils/queryUtils';
import pathsManifest, { RMW_PATH } from './pathsManifest';

// page containers
import FrontPage from './containers/FrontPage/Loadable';
import LandingPage from './containers/LandingPage';
import LeadPage from './containers/LeadPage/Loadable';
import LearnMoreLeadGuaranteePage from './containers/LearnMoreLeadGuaranteePage/Loadable';
import ListingLandingPage from './containers/ListingLandingPage/Loadable';
import ListingLandingPageV2 from './containers/ListingLandingPageV2/Loadable';
import NotFoundPage from './containers/NotFoundPage/Loadable';
import ContactsPage from './containers/ContactsPage/Loadable';
import AnalyticsPage from './containers/AnalyticsPage/Loadable';
import BillingPage from './containers/BillingPage/Loadable';
import UserManagementPage from './containers/UserManagementPage/Loadable';
import AddUserPage from './containers/AddUserPage';
import InvoicePage from './containers/InvoicePage/Loadable';
import InvoicePaymentPage from './containers/InvoicePaymentPage/Loadable';
import AccountPaymentPage from './containers/AccountPaymentPage/Loadable';
import UserManagementInvitationPage from './containers/UserManagementInvitationPage';
import EmailVerificationPage from './containers/EmailVerificationPage/Loadable';
import ExperimentsPage from './containers/ExperimentsPage';
import SelectPropertyPage from './containers/SelectPropertyPage/Loadable';
import TenantsPage from './containers/TenantsPage/Loadable';
import PaymentPage from './containers/paidInclusion/PaymentPage';
import InquiryContactPage from './containers/InquiryContactPage';
import IpCheckPage from './containers/IpCheckPage/Loadable';
import RentGuaranteePage from './containers/RentGuaranteePage/Loadable';

// conditional import to skip loadable
let PropertiesPage; let
    PropertyDetailsPage = null;
function importComponents(component) {
    if (component === 'propertiesPage' && __SERVER__) {
        PropertiesPage = require('./containers/PropertiesPage/').default;
    } else if (component === 'propertyDetailsPage' && __SERVER__) {
        PropertyDetailsPage = require('./containers/PropertyDetailsPage/');
    } else {
        PropertiesPage = require('./containers/PropertiesPage/Loadable').default;
        PropertyDetailsPage = require('./containers/PropertyDetailsPage/').default;
    }
}

export default ({
    brand,
    isRunningInProd,
    pathname,
} = {}) => {
    const paths = pathsManifest.getAllPaths(brand);
    const allowParamsRegex = StringConstants.ALLOW_PARAMS_REGEX;
    const allowTenantsParamsRegex = StringConstants.ALLOW_TENANTS_PARAMS_REGEX;
    const zillowBrand = StringConstants.UNFORMATTED_BRAND_NAMES.zillow;
    const component = findKey(paths, path => path === pathname);

    importComponents(component);

    /*
     * NOTE: react route render props are undefined when hitting the route directly
     * renderProps.component is undefined server side in authenticate.js
     * bypassing getUser to set the user model
     *
     * Wrapping redirect component within a function fixes the issue.
     * https://github.com/ReactTraining/react-router/issues/4942
     */
    const redirectRouteList = {
        editListing: {
            path: `${paths.editListingPage}/:propertyId*${allowParamsRegex}`,
            getRedirectRoute: () => (
                <Route
                    render={props => (
                        <Redirect to={`${paths.propertiesPage}/${props.match.params.propertyId}/listing`} />
                    )}
                />
            ),

        },
        statistics: {
            path: `${paths.statisticsPage}/:propertyId*${allowParamsRegex}`,
            getRedirectRoute: () => (
                <Route
                    render={props => (
                        <Redirect to={`${paths.propertiesPage}/${props.match.params.propertyId}/analytics`} />
                    )}
                />
            ),

        },
        payments: {
            path: `${paths.propertiesPage}/:propertyId/payments${allowParamsRegex}`,
            getRedirectRoute: () => (
                <Route
                    render={props => (
                        <Redirect to={`/rental-manager/payments/properties/${props.match.params.propertyId}`} />
                    )}
                />
            ),
        },
        leases: {
            path: `${paths.propertiesPage}/:propertyId/leases${allowParamsRegex}`,
            getRedirectRoute: () => (
                <Route
                    render={props => (
                        <Redirect to={`/rental-manager/leasing/property/${props.match.params.propertyId}`} />
                    )}
                />
            ),
        },
        propertyDetailsStatistics: {
            path: `${paths.propertiesPage}/:propertyId/statistics${allowParamsRegex}`,
            getRedirectRoute: () => (
                <Route
                    render={props => (
                        <Redirect to={`${paths.propertiesPage}/${props.match.params.propertyId}/analytics`} />
                    )}
                />
            ),

        },
        propertyDetailsInbox: {
            path: `${paths.propertiesPage}/:propertyId/inbox${allowParamsRegex}`,
            getRedirectRoute: () => (
                <Route
                    render={props => (
                        <Redirect to={`${paths.propertiesPage}/${props.match.params.propertyId}/contacts`} />
                    )}
                />
            ),

        },
        leads: {
            path: `${paths.leadsPage}/:propertyId*${allowParamsRegex}`,
            getRedirectRoute: () => (
                <Route
                    render={props => (
                        <Redirect to={`${paths.propertiesPage}/${props.match.params.propertyId}/contacts`} />
                    )}
                />
            ),

        },
        propertyLeadsPage: {
            path: `${paths.propertiesPage}/:propertyId/leads${allowParamsRegex}`,
            getRedirectRoute: () => (
                <Route
                    render={props => (
                        <Redirect to={`${paths.propertiesPage}/${props.match.params.propertyId}/contacts`} />
                    )}
                />
            ),

        },
        listingTeardownPage: {
            path: `${paths.listingTeardownPage}${allowParamsRegex}`,
            getRedirectRoute: () => (
                <Route
                    exact={true}
                    render={(props) => {
                        const queryParams = queryUtils.parse(props.location.search);
                        const encodedAlias = queryParams[QueryConstants.ENCODED_ALIAS];
                        const redirectUrl = encodedAlias
                            ? `${paths.propertiesPage}/${encodedAlias}/listing`
                            : paths.propertiesPage;
                        return <Redirect to={redirectUrl} />;
                    }}
                />
            ),
        },
    };

    /*
     * NOTE: Adding regex ? to end of paths because v4 router does not handle this well.
     * Allows: ?, ?myparam, ?myparam=, ?myparam=hello, ?myparam=hello&something.
     * Disallows: /my-listingsPlusExtraChars
     * https://github.com/ReactTraining/react-router/issues/5381
     */
    return (
        <Switch>
            {/* redirect /my-listings to /properties */}
            <Route
                path={`${paths.myListingsPage}${allowParamsRegex}`}
                render={() => <Redirect to={`${paths.propertiesPage}`} />}
            />
            { /* email-verification */}
            <Route
                path={`${paths.emailVerification}${allowParamsRegex}`}
                component={EmailVerificationPage}
            />
            {/* select-property */}
            {/* redirect logic is in matchRoute.js  */}
            <Route
                component={SelectPropertyPage}
                exact={true}
                path={`/rental-manager/select-property${allowParamsRegex}`}
            />

            {/* tenants page - base */}
            {/* sub routes within page: find-tenants, manage-tenants */}
            {/* allow access even when canAccessConnectedExperience is false
                PermissionCheck will redirect to the Properties page
            */}
            <Route
                component={TenantsPage}
                exact={true}
                path={`${paths.propertiesPage}/:propertyId/${allowTenantsParamsRegex}${allowParamsRegex}`}
            />

            {/* properties */}
            <Route
                component={PropertiesPage}
                exact={true}
                path={`${paths.propertiesPage}${allowParamsRegex}`}
            />

            {/* property - learn more - paid inclusion */}
            <Route
                component={LearnMoreLeadGuaranteePage}
                exact={true}
                path={`${paths.propertiesPage}/:propertyId/learn-more-paid-inclusion${allowParamsRegex}`}
            />

            {/* property details - edit */}
            <Route
                component={PropertyDetailsPage}
                exact={true}
                path={`${paths.propertiesPage}/:propertyId/listing${allowParamsRegex}`}
            />

            {/* property details - applications */}
            <Route
                pageWithSensitiveInformation={true}
                component={PropertyDetailsPage}
                exact={true}
                path={`${paths.propertiesPage}/:propertyId/applications${allowParamsRegex}`}
            />

            {/* property details - payments */}
            {/* redirect /rental-manager/properties/:propertyId/payments to /rental-manager/payments/properties/:propertyId */}
            <Route exact={true} path={redirectRouteList.payments.path}>
                {redirectRouteList.payments.getRedirectRoute()}
            </Route>

            {/* property details - leases */}
            {/* redirect /rental-manager/properties/:propertyId/leases to /rental-manager/leasing/property/:propertyId */}
            <Route exact={true} path={redirectRouteList.leases.path}>
                {redirectRouteList.leases.getRedirectRoute()}
            </Route>

            {/* property details - contacts */}
            <Route
                component={PropertyDetailsPage}
                exact={true}
                path={`${paths.propertiesPage}/:propertyId/contacts${allowParamsRegex}`}
            />

            {/* property details - analytics */}
            <Route
                component={PropertyDetailsPage}
                exact={true}
                path={`${paths.propertiesPage}/:propertyId/analytics${allowParamsRegex}`}
            />

            {/* property details - reviews */}
            <Route
                component={PropertyDetailsPage}
                exact={true}
                path={`${paths.propertiesPage}/:propertyId/reviews${allowParamsRegex}`}
            />

            {/* redirect /user/edit-listing/:propertyId to /properties/:propertyId/listing */}
            <Route exact={true} path={redirectRouteList.editListing.path}>
                {redirectRouteList.editListing.getRedirectRoute()}
            </Route>

            {/* Legacy route v1: leads for listing item */}
            {/* redirect /rental-manager/leads/:propertyId to /rental-manager/properties/:propertyId/contacts */}
            <Route exact={true} path={redirectRouteList.leads.path}>
                {redirectRouteList.leads.getRedirectRoute()}
            </Route>

            {/* Legacy route v2: leads for listing item */}
            {/* redirect /rental-manager/properties/:propertyId/inbox to /rental-manager/properties/:propertyId/contacts */}
            <Route exact={true} path={redirectRouteList.propertyDetailsInbox.path}>
                {redirectRouteList.propertyDetailsInbox.getRedirectRoute()}
            </Route>

            {/* Legacy route v2: leads for listing item */}
            {/* redirect /rental-manager/properties/:propertyId/leads to /rental-manager/properties/:propertyId/inbox */}
            <Route exact={true} path={redirectRouteList.propertyLeadsPage.path}>
                {redirectRouteList.propertyLeadsPage.getRedirectRoute()}
            </Route>

            {/* lead item */}
            <Route component={LeadPage} exact={true} path={`${paths.leadPage}/:leadId`} />

            {/* Legacy route v1: statistics */}
            {/* redirect /rental-manager/statistics/:propertyId to /rental-manager/properties/:propertyId/statistics */}
            <Route exact={true} path={redirectRouteList.statistics.path}>
                {redirectRouteList.statistics.getRedirectRoute()}
            </Route>

            {/* Legacy route v2: statistics */}
            {/* redirect /rental-manager/properties/:propertyId/statistics to /rental-manager/properties/:propertyId/analytics */}
            <Route exact={true} path={redirectRouteList.propertyDetailsStatistics.path}>
                {redirectRouteList.propertyDetailsStatistics.getRedirectRoute()}
            </Route>

            {/* legacy route: listing teardown */}
            <Route exact={true} path={redirectRouteList.listingTeardownPage.path}>
                {redirectRouteList.listingTeardownPage.getRedirectRoute()}
            </Route>

            {/* contacts */}
            <Route component={ContactsPage} exact={true} path={`${paths.contactsPage}${allowParamsRegex}`} />

            {/* analytics subroutes */}
            {/* analytics - overview */}
            <Route
                component={AnalyticsPage}
                exact={true}
                path={`${paths.analyticsPage}/${paths.analyticsOverview}${allowParamsRegex}`}
            />

            {/* analytics - properties */}
            <Route
                component={AnalyticsPage}
                exact={true}
                path={`${paths.analyticsPage}/${paths.analyticsFunnel}${allowParamsRegex}`}
            />

            {/* analytics - adspend */}
            <Route
                component={AnalyticsPage}
                exact={true}
                path={`${paths.analyticsPage}/${paths.analyticsAdSpend}${allowParamsRegex}`}
            />

            {/* analytics - market */}
            <Route
                component={AnalyticsPage}
                exact={true}
                path={`${paths.analyticsPage}/market${allowParamsRegex}`}
            />

            {/* billings - due */}
            <Route
                component={BillingPage}
                exact={true}
                path={`${paths.billingPage}/${paths.billingDue}${allowParamsRegex}`}
            />

            {/* billings - paid */}
            <Route
                component={BillingPage}
                exact={true}
                path={`${paths.billingPage}/${paths.billingPaid}${allowParamsRegex}`}
            />

            {/* billings - invoice */}
            <Route
                component={InvoicePage}
                exact={true}
                path={`${paths.billingPage}/${paths.invoicePage}/:invoiceId`}
            />

            {/* billings - invoice - pay */}
            <Route
                component={InvoicePaymentPage}
                exact={true}
                path={`${paths.billingPage}/${paths.invoicePage}/:invoiceId/payment`}
            />

            <Route
                component={AccountPaymentPage}
                exact={true}
                path={`${paths.billingPage}/account/:accountNumber`}
            />

            {/* billings - Payment settings */}
            <Route
                component={BillingPage}
                exact={true}
                path={`${paths.billingPage}/${'paymentSettings'}`}
            />

            {/* billings - default -due */}
            <Route
                render={() => <Redirect to={`${paths.billingPage}/${paths.billingDue}`} />}
                exact={true}
                path={`${paths.billingPage}${allowParamsRegex}`}
            />

            {/* user-management - default path */}
            <Route
                render={() => <Redirect to={`${paths.userManagementPage}/${paths.userManagementUsers}`} />}
                exact={true}
                path={`${paths.userManagementPage}${allowParamsRegex}`}
            />

            {/* user-management - users */}
            <Route
                component={UserManagementPage}
                exact={true}
                path={`${paths.userManagementPage}/${paths.userManagementUsers}${allowParamsRegex}`}
            />

            {/* user-management - pending */}
            <Route
                component={UserManagementPage}
                exact={true}
                path={`${paths.userManagementPage}/${paths.userManagementPending}${allowParamsRegex}`}
            />

            {/* user-management - activity */}
            <Route
                component={UserManagementPage}
                exact={true}
                path={`${paths.userManagementPage}/${paths.userManagementActivity}${allowParamsRegex}`}
            />

            {/* user-management - unrecognized */}
            <Route
                component={UserManagementPage}
                exact={true}
                path={`${paths.userManagementPage}/${paths.userManagementUnrecognizedUsers}${allowParamsRegex}`}
            />

            {/* user-management - users - add */}
            <Route
                component={AddUserPage}
                exact={true}
                path={`${paths.userManagementPage}/${paths.userManagementAddUser}${allowParamsRegex}`}
            />

            {/* user-management - users - edit */}
            <Route
                component={AddUserPage}
                exact={true}
                path={`${paths.userManagementPage}/${paths.userManagementEditUser}${allowParamsRegex}`}
            />

            {/* user-management - accept invite, temporary path */}
            <Route
                component={UserManagementInvitationPage}
                exact={true}
                path={`${paths.userManagementPage}/invitation${allowParamsRegex}`}
            />

            {/* settings - base */}
            {brand === zillowBrand && (
                <Route
                    component={ExperimentsPage}
                    exact={true}
                    path={RMW_PATH.LABSTER_DASHBOARD}
                />
            )}

            {/* settings - base */}
            {brand === zillowBrand && (
                <Route
                    pageWithSensitiveInformation={true}
                    component={SettingsPage}
                    exact={true}
                    path={`${paths.settingsPage}${allowParamsRegex}`}
                />
            )}

            {/* settings - 2fa */}
            {brand === zillowBrand && (
                <Route
                    pageWithSensitiveInformation={true}
                    component={TwoFactorAuthPage}
                    exact={true}
                    path={`${paths.twoFactorAuthPage}${allowParamsRegex}`}
                />
            )}

            {/* paid inclusion payment */}
            <Route
                component={PaymentPage}
                exact={true}
                path={`${paths.propertiesPage}/:propertyId/payToActivate${allowParamsRegex}`}
            />

            {/* inquiry contact */}
            <Route
                component={InquiryContactPage}
                exact={true}
                path={`${paths.inquiryContactPage}`}
            />

            {/* listing claiming */}
            <Route
                component={ListingClaimPage}
                exact={true}
                path={`${RMW_PATH.LISTING_CLAIMING}${allowParamsRegex}`}
            />

            {/* rent guarantee */}
            <Route
                component={RentGuaranteePage}
                exact={true}
                path={`${RMW_PATH.RENT_GUARANTEE}${allowParamsRegex}`}
            />

            {/* zillow routes */}
            {/* https://stackoverflow.com/questions/43289132/react-router-v4-global-no-match-to-nested-route-childs/43301397#comment74979219_43831238 */}
            {/* redirect /post-a-listing to properties page and pop modal */}
            {brand === zillowBrand
                ? [
                    <Route key={1} component={LandingPage} exact={true} path={`${paths.base}([/])?${allowParamsRegex}`} />,

                    <Route
                        key={2}
                        exact={true}
                        component={ListingLandingPage}
                        path={`${paths.postAListingPage}([/])${allowParamsRegex}`}
                    />,

                    <Route
                        key={4}
                        exact={true}
                        component={ListingLandingPageV2}
                        path={`${paths.postAListingPage}-v2([/])${allowParamsRegex}`}
                    />,

                    // Legacy ZRM redirect: /create/listing to /properties
                    // Need to use addQueryParams here, which doesn't work in legacyRouteRedirects
                    <Route
                        path={`${paths.legacyZRM.createListing}/(.*)?`}
                        key={3}
                        addQueryParams={{ postingPath: 'true' }}
                        render={() => <Redirect to={{ pathname: paths.propertiesPage }} />}
                    />,
                ]
                : [
                    <Route
                        key={3}
                        path={`${paths.postAListingPage}${allowParamsRegex}`}
                        addQueryParams={{ postingPath: 'true' }} // value must be a string
                        render={() => <Redirect to={{ pathname: paths.propertiesPage }} />}
                    />,
                    <Route key={4} component={FrontPage} exact={true} path={`${paths.base}([/])?${allowParamsRegex}`} />,
                ]}

            {/* marketing routes */}
            {/* payments */}
            <Route
                component={PropertiesPage}
                exact={true}
                path={`${paths.marketingRoutes.onboarding.payments}${allowParamsRegex}`}
            />
            {/* applications */}
            <Route
                component={PropertiesPage}
                exact={true}
                path={`${paths.marketingRoutes.onboarding.applications}${allowParamsRegex}`}
            />
            {/* screening */}
            <Route
                ssrRedirect={paths.marketingRoutes.screeningLangingPage}
                redirectStatus={301}
                component={PropertiesPage}
                exact={true}
                path={`${paths.marketingRoutes.screening}${allowParamsRegex}`}
            />
            {/* collect-rent-online */}
            <Route
                ssrRedirect={paths.marketingRoutes.collectRentLandingPage}
                redirectStatus={301}
                component={PropertiesPage}
                exact={true}
                path={`${paths.marketingRoutes.collectRent}${allowParamsRegex}`}
            />

            <Route
                component={IpCheckPage}
                exact={true}
                path={RMW_PATH.IP_CHECK_PAGE}
            />

            {/* legacy ZRM route redirects */}
            {brand === zillowBrand && __SERVER__ && legacyRouteRedirects(paths)}

            {/* feature flag page - non-prod environments */}
            {!isRunningInProd && <Route component={FeatureFlagPage} path={`${paths.featureFlagPage}${allowParamsRegex}`} />}

            {/** 404 **/}
            <Route path="*" exact={true} component={NotFoundPage} />
        </Switch>
    );
};
